import { useCallback, useEffect, useMemo, useState } from 'react';
import { creationRequestsAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import {
  ACCESS_NOT_REQUIRED,
  DEFAULT_PHONE_PREFIX,
  ONBOARDING_FORM_CATALOG,
  emptyOnboardingForm,
  indianMobileDigits,
  toggleExclusiveChoice,
} from '../domain/userCreationRequest/onboardingFormOptions';

export default function useVeOnboardingForm() {
  const [formData, setFormData] = useState(emptyOnboardingForm);
  const [catalog, setCatalog] = useState({
    categories: [],
    form: ONBOARDING_FORM_CATALOG,
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [disabled, setDisabled] = useState(false);

  const form = catalog.form || ONBOARDING_FORM_CATALOG;
  const categories = useMemo(() => catalog.categories || [], [catalog.categories]);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await creationRequestsAPI.catalog();
      const data = res.data?.data || {};
      setCatalog({
        categories: data.categories || [],
        form: data.form || ONBOARDING_FORM_CATALOG,
      });
      setDisabled(false);
    } catch (err) {
      if (err.response?.data?.code === 'FEATURE_DISABLED') {
        setDisabled(true);
        setError('Account requests are not available right now.');
      } else if (err.response?.status === 429) {
        setError(getApiErrorMessage(err, 'Too many account requests. Please try again later.'));
      } else {
        setError(getApiErrorMessage(err, 'Could not load the onboarding form'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const onChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      if (name === 'phone') {
        return { ...prev, phone: indianMobileDigits(value) };
      }
      return { ...prev, [name]: value };
    });
  }, []);

  const onToggleList = useCallback((name, value, exclusiveId) => {
    setFormData((prev) => ({
      ...prev,
      [name]: toggleExclusiveChoice(prev[name], value, exclusiveId),
    }));
  }, []);

  const submit = useCallback(
    async (event) => {
      event?.preventDefault?.();
      setSubmitting(true);
      setError('');
      setSuccess('');
      try {
        const selectedCategory = categories.find(
          (item) => String(item.id) === String(formData.courseCategoryId)
        );
        await creationRequestsAPI.submit({
          title: formData.title,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: `${form.defaultPhonePrefix || DEFAULT_PHONE_PREFIX}${formData.phone}`,
          courseCategoryId: formData.courseCategoryId,
          courseCategoryName: selectedCategory?.categoryName || '',
          modeOfBatch: formData.modeOfBatch,
          totalFeesInr: formData.totalFeesInr,
          needWelcomeEmail: formData.needWelcomeEmail,
          needMyAcademyAccess: formData.needMyAcademyAccess,
          toolAccess: formData.toolAccess,
          toolAccessDurationWeeks: Number(formData.toolAccessDurationWeeks),
          toolAccessStartDate: formData.toolAccessStartDate,
          specialMachineVnc: formData.specialMachineVnc,
          vncTools: formData.vncTools,
          puttyTools: formData.puttyTools,
          specialVncTools: formData.specialVncTools,
          pdkGrpPermissions: formData.pdkGrpPermissions,
          labsCopyHome: formData.labsCopyHome,
          labsAccess: formData.labsAccess,
        });
        setSuccess('Request submitted. Our team will review it and create your account if it is approved.');
        setFormData(emptyOnboardingForm());
      } catch (err) {
        if (err.response?.status === 429) {
          setError(getApiErrorMessage(err, 'Too many account requests. Please try again later.'));
        } else {
          setError(getApiErrorMessage(err, 'Could not submit onboarding'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [categories, form.defaultPhonePrefix, formData]
  );

  return {
    formData,
    form,
    categories,
    loading,
    submitting,
    error,
    success,
    disabled,
    accessNotRequired: form.accessNotRequired || ACCESS_NOT_REQUIRED,
    onChange,
    onToggleList,
    submit,
  };
}
